/**
 * Wire boundary for API v1.
 *
 * The contract is **frozen** as described by `olr/server/olr/schemas.py` and
 * `olr/docs/API.md`:
 *
 * - vocabulary: `text`, `source_language` / `target_language`, `tags` as a comma-separated string;
 * - `/auth/me` returns `UserOut` directly;
 * - settings are flat (`SettingsOut`);
 * - Anki: `{reachable, version, error, url}`, `{created, updated, unchanged, failed, dry_run, notes}`;
 * - `/translate` always returns `translations[]` (plus `translated_text` for the single form).
 *
 * These functions turn `unknown` JSON into the typed shapes in `types.ts`: they
 * fill defaults for absent fields and coerce nothing silently — a wrong type
 * becomes the documented default rather than leaking into the UI.
 */
import { asBoolean, asNullableString, asNumber, asString, isRecord, pick } from '../lib/guards';
import type {
  AnkiNotePayload,
  AnkiPingResponse,
  AnkiStatus,
  AnkiSyncResult,
  Settings,
  UserOut,
  Vocab,
  VocabList,
} from './types';

export function normalizeUser(raw: unknown): UserOut {
  if (!isRecord(raw)) {
    return { id: 0, email: '', display_name: null, created_at: '' };
  }
  return {
    id: asNumber(pick(raw, 'id')),
    email: asString(pick(raw, 'email')),
    display_name: asNullableString(pick(raw, 'display_name')),
    created_at: asString(pick(raw, 'created_at')),
  };
}

export function normalizeVocab(raw: unknown): Vocab {
  if (!isRecord(raw)) throw new Error('normalizeVocab: expected a vocabulary object');
  const mediaTimestamp = pick(raw, 'media_timestamp');
  const tags = pick(raw, 'tags');

  return {
    id: asNumber(pick(raw, 'id')),
    text: asString(pick(raw, 'text')),
    lemma: asNullableString(pick(raw, 'lemma')),
    translation: asNullableString(pick(raw, 'translation')),
    context: asNullableString(pick(raw, 'context')),
    context_before: asNullableString(pick(raw, 'context_before')),
    context_after: asNullableString(pick(raw, 'context_after')),
    source_language: asString(pick(raw, 'source_language')),
    target_language: asString(pick(raw, 'target_language')),
    note: asNullableString(pick(raw, 'note')),
    // `tags` is a string column; tolerate a list in case the API ever normalises it.
    tags: Array.isArray(tags)
      ? tags.filter((tag): tag is string => typeof tag === 'string').join(',')
      : asNullableString(tags),
    source_url: asNullableString(pick(raw, 'source_url')),
    media_title: asNullableString(pick(raw, 'media_title')),
    media_kind: asNullableString(pick(raw, 'media_kind')),
    media_timestamp: typeof mediaTimestamp === 'number' && Number.isFinite(mediaTimestamp) ? mediaTimestamp : null,
    occurrence_count: asNumber(pick(raw, 'occurrence_count'), 1),
    created_at: asString(pick(raw, 'created_at')),
    updated_at: asString(pick(raw, 'updated_at')),
  };
}

export function normalizeVocabList(raw: unknown): VocabList {
  if (!isRecord(raw)) return { items: [], total: 0, limit: 0, offset: 0 };
  const items = Array.isArray(raw.items) ? raw.items.map(normalizeVocab) : [];
  return {
    items,
    total: asNumber(pick(raw, 'total'), items.length),
    limit: asNumber(pick(raw, 'limit'), items.length),
    offset: asNumber(pick(raw, 'offset'), 0),
  };
}

export function normalizeSettings(raw: unknown): Settings {
  if (!isRecord(raw)) throw new Error('normalizeSettings: expected a settings object');
  return {
    source_language: asString(pick(raw, 'source_language'), 'auto'),
    target_language: asString(pick(raw, 'target_language'), 'en'),
    translation_provider: asNullableString(pick(raw, 'translation_provider')),
    interface_language: asString(pick(raw, 'interface_language'), 'ru'),
    show_native_transcription: asBoolean(pick(raw, 'show_native_transcription')),
    auto_save_words: asBoolean(pick(raw, 'auto_save_words')),
    subtitle_font_scale: asNumber(pick(raw, 'subtitle_font_scale'), 1),
    subtitle_opacity: asNumber(pick(raw, 'subtitle_opacity'), 1),
    auto_pause: asBoolean(pick(raw, 'auto_pause')),
    anki_enabled: asBoolean(pick(raw, 'anki_enabled')),
    anki_deck: asString(pick(raw, 'anki_deck'), 'OLR'),
    anki_model: asString(pick(raw, 'anki_model'), 'Basic'),
    anki_tags: asString(pick(raw, 'anki_tags')),
    updated_at: asString(pick(raw, 'updated_at')),
  };
}

/** A 503 from `/anki/ping` carries the same body with `reachable: false`. */
export function normalizeAnkiPing(raw: unknown, fallbackUrl: string): AnkiPingResponse {
  if (!isRecord(raw)) {
    return { reachable: false, version: null, error: null, url: fallbackUrl };
  }
  const version = pick(raw, 'version');
  return {
    reachable: asBoolean(pick(raw, 'reachable')),
    version: typeof version === 'number' ? version : null,
    error: asNullableString(pick(raw, 'error')),
    url: asString(pick(raw, 'url'), fallbackUrl),
  };
}

export function normalizeAnkiStatus(raw: unknown): AnkiStatus {
  if (!isRecord(raw)) {
    return {
      reachable: false,
      version: null,
      error: null,
      url: '',
      linked_notes: 0,
      last_sync_at: null,
      deck: '',
      model: '',
    };
  }
  const version = pick(raw, 'version');
  return {
    reachable: asBoolean(pick(raw, 'reachable')),
    version: typeof version === 'number' ? version : null,
    error: asNullableString(pick(raw, 'error')),
    url: asString(pick(raw, 'url')),
    linked_notes: asNumber(pick(raw, 'linked_notes')),
    last_sync_at: asNullableString(pick(raw, 'last_sync_at')),
    deck: asString(pick(raw, 'deck')),
    model: asString(pick(raw, 'model')),
  };
}

function normalizeNote(raw: unknown): AnkiNotePayload {
  if (!isRecord(raw)) {
    return { note_key: '—', action: 'add', note_id: null, fields: {}, tags: [], deck: '', model: '' };
  }
  const fields = pick(raw, 'fields');
  const tags = pick(raw, 'tags');
  const noteId = pick(raw, 'note_id');
  return {
    note_key: asString(pick(raw, 'note_key'), '—'),
    action: pick(raw, 'action') === 'update' ? 'update' : 'add',
    note_id: typeof noteId === 'number' ? noteId : null,
    fields: isRecord(fields)
      ? Object.fromEntries(Object.entries(fields).map(([key, value]) => [key, asString(value)]))
      : {},
    tags: Array.isArray(tags) ? tags.filter((tag): tag is string => typeof tag === 'string') : [],
    deck: asString(pick(raw, 'deck')),
    model: asString(pick(raw, 'model')),
  };
}

export function normalizeAnkiSync(raw: unknown): AnkiSyncResult {
  if (!isRecord(raw)) {
    return { created: 0, updated: 0, unchanged: 0, failed: 0, errors: [], dry_run: false, notes: [] };
  }
  const errors = Array.isArray(raw.errors)
    ? raw.errors.filter((entry): entry is string => typeof entry === 'string')
    : [];
  return {
    created: asNumber(pick(raw, 'created')),
    updated: asNumber(pick(raw, 'updated')),
    unchanged: asNumber(pick(raw, 'unchanged')),
    failed: asNumber(pick(raw, 'failed')),
    errors,
    dry_run: asBoolean(pick(raw, 'dry_run')),
    notes: Array.isArray(raw.notes) ? raw.notes.map(normalizeNote) : [],
  };
}
